import { FullScreenLoader } from "@repo/app-shell"
import { api } from "@repo/db"
import { Card, CardContent, CardHeader, CardTitle } from "@repo/shad/ui/card"
import { Wallpaper } from "@repo/ui"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { useMutation } from "convex/react"
import { Building2, Shield } from "lucide-react"

import { useConvex } from "@/hooks/use-convex"
import { convexClient, queryWithTimeout } from "@/lib/convex"

export const Route = createFileRoute("/check-dealership")({
  beforeLoad: async () => {
    const user = await queryWithTimeout(convexClient.query(api.auth.getCurrentUser))
    if (!user) {
      throw redirect({ to: "/login", search: { redirect: undefined } })
    }
    if (!user.name || !user.email) {
      throw redirect({ to: "/complete-profile" })
    }
    if (user.isDemo && !user.dealership) {
      throw redirect({ to: "/demo" })
    }
    // Fast path: regular user with a dealership — skip the component entirely
    if (!user.isSuperAdmin && user.dealership) {
      throw redirect({
        to: "/$dealership/dashboard",
        params: { dealership: user.dealership.slug },
      })
    }
    if (!user.isSuperAdmin && !user.dealership) {
      throw redirect({ to: "/create-dealership" })
    }
  },
  component: SuperAdminPicker,
})

function SuperAdminPicker() {
  const navigate = useNavigate()
  const { data: allDealerships } = useConvex(api.dealerships.queries.listAll, {})
  const setActiveDealership = useMutation(api.users.mutations.setActiveDealership)

  if (!allDealerships) {
    return <FullScreenLoader />
  }

  return (
    <Wallpaper
      color="blue"
      className="min-h-svh flex items-center justify-center p-6"
      innerClassName="w-full flex justify-center"
    >
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <CardTitle>Super Admin — Select Dealership</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {allDealerships.length === 0 ? (
              <p className="text-sm text-muted-foreground">No dealerships found.</p>
            ) : (
              allDealerships.map((d) => (
                <button
                  key={d._id}
                  className="w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent"
                  onClick={async () => {
                    await setActiveDealership({ dealershipId: d._id })
                    void navigate({
                      to: "/$dealership/dashboard",
                      params: { dealership: d.slug },
                    })
                  }}
                >
                  <Building2 className="h-5 w-5 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium truncate">{d.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{d.slug}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </Wallpaper>
  )
}
